import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar as CalendarIcon, Clock, Users, Plus, Edit2, X, CreditCard, History, Trash2, RefreshCw, AlertTriangle, ChevronLeft, ChevronRight, StickyNote } from "lucide-react";
import { Sidebar } from "@/components/Navigation";
import BottomNav from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { format, addDays } from "date-fns";
import { serializeDateOnly } from "@/lib/dateOnly";
import { BookingDialog } from "@/components/calendar/BookingDialog";
import { BookingDetailsDialog } from "@/components/calendar/BookingDetailsDialog";
import { ResourcesDialog } from "@/components/calendar/ResourcesDialog";
import { EditSessionDialog } from "@/components/calendar/EditSessionDialog";

const PAYMENT_STATUS_COLORS = {
  paid: { bg: "#DCFCE7", border: "#16A34A", text: "text-green-700", badge: "bg-green-100 text-green-700", icon: "✓" },
  partial: { bg: "#D1FAE5", border: "#059669", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-700", icon: "◐" },
  pending: { bg: "#ECFDF5", border: "#10B981", text: "text-emerald-600", badge: "bg-emerald-50 text-emerald-600", icon: "○" },
};

const StudioCalendar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  // Phase-3: who can write to the calendar (create/edit rentals, +slot buttons)?
  // Admins always; instructors only when permissions.calendar_access === "edit".
  const canEditCalendar =
    user?.role === "admin" ||
    user?.role === "super_admin" ||
    user?.is_super_admin === true ||
    (user?.role === "instructor" && user?.permissions?.calendar_access === "edit");
  const [studios, setStudios] = useState([]);
  const [selectedStudio, setSelectedStudio] = useState(null);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [calendarData, setCalendarData] = useState(null);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [openBooking, setOpenBooking] = useState(false);
  const [openResource, setOpenResource] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [openEditSession, setOpenEditSession] = useState(false);
  const [editingSessionItem, setEditingSessionItem] = useState(null);
  const [editingBatchDefaults, setEditingBatchDefaults] = useState(null);
  const [instructorOptions, setInstructorOptions] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [editingResource, setEditingResource] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailsTab, setDetailsTab] = useState("info");
  
  // Booking form
  const [bookingForm, setBookingForm] = useState({
    renter_name: "", renter_phone: "", renter_email: "",
    start_time: "", end_time: "", agreed_rent: "",
    discount: "0", discount_type: "flat",
    resources_required: [], notes: "",
    color: "",  // Feb 2026 — optional calendar tint; "" = use status-based fallback
    // Payment at creation
    payment_status: "pending", amount_paid: "",
    payment_method: "cash", payment_date: format(new Date(), 'yyyy-MM-dd'),
    reference_number: "", payment_notes: "",
    // Recurring
    is_recurring: false, recurrence_type: "weekly", custom_weeks: "2",
    recurrence_end_type: "occurrences", recurrence_end_date: "", num_occurrences: "4",
    // v2 multi-day recurrence (additive — used when recurrence_type === "weekly")
    repeat_interval: "1",
    repeat_weekdays: [],        // 0=Mon..6=Sun; auto-seeded with start_date weekday when toggled on
    ends_kind: "after_weeks",   // "after_weeks" | "on_date"
    ends_weeks: "6",
    ends_date: "",
  });
  
  // Edit form
  const [editForm, setEditForm] = useState({});
  
  // Record payment form
  const [payForm, setPayForm] = useState({
    amount: "", payment_method: "cash", payment_date: format(new Date(), 'yyyy-MM-dd'),
    reference_number: "", notes: "",
  });
  
  // Recurring preview
  const [recurringPreview, setRecurringPreview] = useState(null);
  const [recurringLoading, setRecurringLoading] = useState(false);
  
  const [resourceForm, setResourceForm] = useState({ name: "", quantity: "" });

  // Customer auto-suggest
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const customerSearchTimeout = React.useRef(null);

  useEffect(() => { fetchStudios(); }, []);

  const fetchCalendarData = useCallback(async () => {
    if (!selectedStudio) return;
    try {
      const response = await api.get(`/calendar/${selectedStudio.studio_id}?date=${selectedDate}`);
      setCalendarData(response.data);
    } catch (error) {
      toast.error("Failed to load calendar");
    }
  }, [selectedStudio, selectedDate]);

  const fetchResources = useCallback(async () => {
    if (!selectedStudio) return;
    try {
      const response = await api.get(`/resources?studio_id=${selectedStudio.studio_id}`);
      setResources(response.data.resources);
    } catch (error) {
      console.error("Failed to load resources");
    }
  }, [selectedStudio]);

  useEffect(() => {
    if (selectedStudio) {
      fetchCalendarData();
      fetchResources();
    }
  }, [selectedStudio, selectedDate, fetchCalendarData, fetchResources]);

  const fetchStudios = async () => {
    try {
      const response = await api.get("/studios");
      const list = response.data.studios || [];
      setStudios(list);
      if (list.length > 0) {
        const activeId = localStorage.getItem("active_studio_id");
        const preferred = (activeId && list.find((s) => s.studio_id === activeId)) || list[0];
        setSelectedStudio(preferred);
      }
    } catch (error) {
      toast.error("Failed to load studios");
    } finally {
      setLoading(false);
    }
  };

  // ========== HELPERS ==========
  // Phase-C: open inline session edit dialog from calendar event click
  const openClassEdit = async (item) => {
    if (!item?.batch_id) return;
    try {
      // Fetch fresh batch defaults so "this & future" save can build the right schedule
      const r = await api.get("/batches");
      const batch = (r.data?.batches || []).find((b) => b.batch_id === item.batch_id);
      if (!batch) {
        toast.error("Batch not found");
        return;
      }
      // Lazy-load instructor list once
      if (instructorOptions.length === 0) {
        try {
          const ir = await api.get("/instructors");
          setInstructorOptions(ir.data?.instructors || []);
        } catch (err) {
          // non-fatal: instructor reassignment dropdown will simply stay empty
          console.warn("[StudioCalendar] failed to load instructors", err);
        }
      }
      setEditingSessionItem(item);
      setEditingBatchDefaults(batch);
      setOpenEditSession(true);
    } catch (e) {
      toast.error("Failed to open class edit");
    }
  };

  const searchCustomers = (query) => {
    if (customerSearchTimeout.current) clearTimeout(customerSearchTimeout.current);
    if (!query || query.length < 2) { setCustomerSuggestions([]); setShowSuggestions(false); return; }
    customerSearchTimeout.current = setTimeout(async () => {
      try {
        const res = await api.get(`/customers?search=${encodeURIComponent(query)}`);
        setCustomerSuggestions(res.data.customers || []);
        setShowSuggestions((res.data.customers || []).length > 0);
      } catch { setCustomerSuggestions([]); }
    }, 300);
  };

  const selectCustomer = (customer) => {
    setBookingForm(prev => ({
      ...prev,
      renter_name: customer.name,
      renter_phone: customer.phone,
      renter_email: customer.email || "",
    }));
    setSelectedCustomerId(customer.customer_id);
    setShowSuggestions(false);
    setCustomerSuggestions([]);
  };

  const calcTotalAmount = (rent, discount, discountType) => {
    const r = parseFloat(rent) || 0;
    const d = parseFloat(discount) || 0;
    if (discountType === "percent") return Math.round(Math.max(r - r * d / 100, 0));
    return Math.round(Math.max(r - d, 0));
  };

  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const formatTime12 = (time24) => {
    if (!time24) return "";
    const [h, m] = time24.split(":");
    const hour = parseInt(h, 10);
    const period = hour >= 12 ? "PM" : "AM";
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${period}`;
  };

  const getItemsForRoom = (roomId) => calendarData?.scheduled_items?.filter(item => item.room_id === roomId && item.status !== "cancelled") || [];
  
  const getSlotSpan = (startTime, endTime) => {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    return Math.ceil((endMinutes - startMinutes) / 30);
  };

  const isSlotOccupied = (roomId, slotTime) => {
    const items = getItemsForRoom(roomId);
    const slotMinutes = timeToMinutes(slotTime);
    return items.find(item => {
      const itemStart = timeToMinutes(item.start_time);
      const itemEnd = timeToMinutes(item.end_time);
      return slotMinutes >= itemStart && slotMinutes < itemEnd;
    });
  };

  // ========== BOOKING CRUD ==========
  const createBooking = async () => {
    if (!bookingForm.renter_name || !bookingForm.renter_phone || !bookingForm.start_time || !bookingForm.end_time || !bookingForm.agreed_rent) {
      toast.error("Please fill all required fields");
      return;
    }
    if (bookingForm.renter_phone.length < 10) {
      toast.error("Phone number must be at least 10 digits");
      return;
    }

    const total = calcTotalAmount(bookingForm.agreed_rent, bookingForm.discount, bookingForm.discount_type);
    const amtPaid = bookingForm.payment_status !== "pending" ? (parseFloat(bookingForm.amount_paid) || 0) : 0;
    
    const paymentHistory = [];
    if (amtPaid > 0) {
      paymentHistory.push({
        amount: amtPaid,
        method: bookingForm.payment_method,
        date: bookingForm.payment_date,
        reference_number: bookingForm.reference_number || null,
        notes: bookingForm.payment_notes || null,
      });
    }

    const payload = {
      studio_id: selectedStudio.studio_id,
      room_id: selectedRoom,
      booking_type: "rental",
      customer_id: selectedCustomerId || null,
      renter_name: bookingForm.renter_name,
      renter_phone: bookingForm.renter_phone,
      renter_email: bookingForm.renter_email || null,
      agreed_rent: parseFloat(bookingForm.agreed_rent),
      discount: parseFloat(bookingForm.discount) || 0,
      discount_type: bookingForm.discount_type,
      amount_paid: amtPaid,
      payment_history: paymentHistory,
      resources_required: bookingForm.resources_required,
      notes: bookingForm.notes,
      booking_date: serializeDateOnly(selectedDate),
      start_time: bookingForm.start_time,
      end_time: bookingForm.end_time,
      color: bookingForm.color || null,
    };

    try {
      await api.post("/bookings", payload);
      toast.success("Room booked successfully!");
      fetchCalendarData();
      setOpenBooking(false);
      resetBookingForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    }
  };

  const resetBookingForm = () => {
    setBookingForm({
      renter_name: "", renter_phone: "", renter_email: "",
      start_time: "", end_time: "", agreed_rent: "",
      discount: "0", discount_type: "flat",
      resources_required: [], notes: "",
      color: "",
      payment_status: "pending", amount_paid: "",
      payment_method: "cash", payment_date: format(new Date(), 'yyyy-MM-dd'),
      reference_number: "", payment_notes: "",
      is_recurring: false, recurrence_type: "weekly", custom_weeks: "2",
      recurrence_end_type: "occurrences", recurrence_end_date: "", num_occurrences: "4",
      repeat_interval: "1", repeat_weekdays: [],
      ends_kind: "after_weeks", ends_weeks: "6", ends_date: "",
    });
    setSelectedRoom(null);
    setRecurringPreview(null);
    setSelectedCustomerId(null);
    setCustomerSuggestions([]);
    setShowSuggestions(false);
  };

  const openBookingDetails = (item) => {
    if (item.type !== "rental" || item.status === "cancelled") return;
    setSelectedBooking(item);
    setCancelConfirmStep(null);
    setCancelFuture(false);
    setEditForm({
      renter_name: item.renter_name || "",
      renter_phone: item.renter_phone || "",
      renter_email: item.renter_email || "",
      start_time: item.start_time || "",
      end_time: item.end_time || "",
      agreed_rent: item.agreed_rent?.toString() || "",
      discount: item.discount?.toString() || "0",
      discount_type: item.discount_type || "flat",
      notes: item.notes || "",
      resources_required: item.resources_required || [],
      color: item.color || "",
      // Apply-to scope for recurring color change (matches room change UX).
      color_change_scope: "this_only",
    });
    setPayForm({
      amount: "", payment_method: "cash", payment_date: format(new Date(), 'yyyy-MM-dd'),
      reference_number: "", notes: "",
    });
    setDetailsTab("info");
    setOpenDetails(true);
  };

  const saveBookingEdit = async () => {
    if (!selectedBooking) return;
    try {
      // Normalize color: "" means "use status fallback" (null on the wire).
      const payload = {
        ...editForm,
        agreed_rent: parseFloat(editForm.agreed_rent) || 0,
        discount: parseFloat(editForm.discount) || 0,
        resources_required: editForm.resources_required || [],
        booking_date: serializeDateOnly(selectedDate),
        color: editForm.color || null,
      };
      // Only send scope when this is a recurring booking + color actually changed.
      if (!selectedBooking.recurring_group_id) {
        delete payload.color_change_scope;
      }
      await api.put(`/bookings/${selectedBooking.booking_id}`, payload);
      toast.success("Booking updated!");
      fetchCalendarData();
      setOpenDetails(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update booking");
    }
  };

  const recordPayment = async () => {
    if (!selectedBooking) return;
    const amt = parseFloat(payForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }

    try {
      const isSeries = !!selectedBooking.recurring_group_id;
      const url = isSeries
        ? `/rent-payments/by-group/${selectedBooking.recurring_group_id}/record`
        : `/rent-payments/${selectedBooking.booking_id}/record`;
      const res = await api.post(url, {
        amount: amt,
        payment_method: payForm.payment_method,
        payment_date: payForm.payment_date,
        reference_number: payForm.reference_number || null,
        notes: payForm.notes || null,
      });
      toast.success("Payment recorded!");
      // Series response returns { group }, single response returns { booking }
      if (isSeries && res.data.group) {
        const g = res.data.group;
        setSelectedBooking({
          ...selectedBooking,
          amount_paid: g.amount_paid,
          balance_amount: g.balance_amount,
          rent_payment_status: g.rent_payment_status,
          payment_history: g.payment_history,
          total_amount: g.total_amount,
        });
      } else if (res.data.booking) {
        setSelectedBooking(res.data.booking);
      }
      setPayForm({ amount: "", payment_method: "cash", payment_date: format(new Date(), 'yyyy-MM-dd'), reference_number: "", notes: "" });
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    }
  };

  const [cancelConfirmStep, setCancelConfirmStep] = useState(null); // null | "confirm" | "refund"

  const initCancelBooking = () => {
    if (!selectedBooking) return;
    if (selectedBooking.recurring_group_id) {
      setCancelConfirmStep("recurring_choice");
    } else {
      const hasPaid = (selectedBooking.amount_paid || 0) > 0;
      if (hasPaid) {
        setCancelConfirmStep("refund");
      } else {
        setCancelConfirmStep("confirm");
      }
    }
  };

  const [cancelFuture, setCancelFuture] = useState(false);

  const executeCancelBooking = async (isRefunded) => {
    try {
      await api.put(`/bookings/${selectedBooking.booking_id}/cancel`, {
        is_refunded: isRefunded,
        cancel_future: cancelFuture,
      });
      toast.success(cancelFuture ? "This and all future bookings cancelled" : "Booking cancelled");
      setCancelConfirmStep(null);
      setCancelFuture(false);
      fetchCalendarData();
      setOpenDetails(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel booking");
      setCancelConfirmStep(null);
      setCancelFuture(false);
    }
  };

  // ========== RECURRING ==========
  const previewRecurring = async () => {
    if (!bookingForm.start_time || !bookingForm.end_time) {
      toast.error("Set start and end time first");
      return;
    }
    setRecurringLoading(true);
    try {
      const payload = {
        studio_id: selectedStudio.studio_id,
        room_id: selectedRoom,
        start_time: bookingForm.start_time,
        end_time: bookingForm.end_time,
        start_date: new Date(selectedDate).toISOString(),
      };
      // v2 multi-day weekly recurrence — use when frequency is weekly AND weekdays selected
      if (bookingForm.recurrence_type === "weekly" && (bookingForm.repeat_weekdays || []).length > 0) {
        payload.weekdays = [...new Set(bookingForm.repeat_weekdays)].sort();
        payload.repeat_interval = Math.max(1, parseInt(bookingForm.repeat_interval) || 1);
        if (bookingForm.ends_kind === "on_date" && bookingForm.ends_date) {
          payload.end_type = "on_date";
          payload.end_value = bookingForm.ends_date;
        } else {
          payload.end_type = "after_weeks";
          payload.end_value = Math.max(1, parseInt(bookingForm.ends_weeks) || 1);
        }
      } else {
        // Legacy single-day path (monthly / custom / no weekdays selected)
        payload.recurrence_type = bookingForm.recurrence_type;
        payload.custom_weeks = parseInt(bookingForm.custom_weeks) || 2;
        if (bookingForm.recurrence_end_type === "end_date" && bookingForm.recurrence_end_date) {
          payload.end_date = new Date(bookingForm.recurrence_end_date).toISOString();
        } else {
          payload.num_occurrences = parseInt(bookingForm.num_occurrences) || 4;
        }
      }
      const res = await api.post("/bookings/recurring/preview", payload);
      setRecurringPreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview");
    } finally {
      setRecurringLoading(false);
    }
  };

  const createRecurringBookings = async () => {
    if (!recurringPreview) return;
    const datesToBook = recurringPreview.dates.filter(d => !d.has_conflict).map(d => d.date);
    if (datesToBook.length === 0) { toast.error("No dates available to book"); return; }

    try {
      const res = await api.post("/bookings/recurring", {
        dates: datesToBook,
        booking: {
          studio_id: selectedStudio.studio_id,
          room_id: selectedRoom,
          customer_id: selectedCustomerId || null,
          renter_name: bookingForm.renter_name,
          renter_phone: bookingForm.renter_phone,
          renter_email: bookingForm.renter_email || null,
          agreed_rent: parseFloat(bookingForm.agreed_rent) || 0,
          discount: parseFloat(bookingForm.discount) || 0,
          discount_type: bookingForm.discount_type,
          start_time: bookingForm.start_time,
          end_time: bookingForm.end_time,
          notes: bookingForm.notes,
          resources_required: bookingForm.resources_required,
          color: bookingForm.color || null,
          recurrence_type: bookingForm.recurrence_type,
          // v2 recurrence intent (persisted on the group for future edit/audit)
          weekdays: bookingForm.recurrence_type === "weekly" && (bookingForm.repeat_weekdays || []).length > 0
            ? [...new Set(bookingForm.repeat_weekdays)].sort()
            : null,
          repeat_interval: bookingForm.recurrence_type === "weekly" && (bookingForm.repeat_weekdays || []).length > 0
            ? Math.max(1, parseInt(bookingForm.repeat_interval) || 1)
            : null,
          end_type: bookingForm.recurrence_type === "weekly" && (bookingForm.repeat_weekdays || []).length > 0
            ? (bookingForm.ends_kind === "on_date" ? "on_date" : "after_weeks")
            : null,
          end_value: bookingForm.recurrence_type === "weekly" && (bookingForm.repeat_weekdays || []).length > 0
            ? (bookingForm.ends_kind === "on_date"
                ? bookingForm.ends_date
                : Math.max(1, parseInt(bookingForm.ends_weeks) || 1))
            : null,
          start_date: new Date(selectedDate).toISOString().slice(0, 10),
          // Series-level payment captured at creation
          payment_status: bookingForm.payment_status,
          amount_paid: bookingForm.payment_status !== "pending"
            ? (parseFloat(bookingForm.amount_paid) || 0)
            : 0,
          payment_method: bookingForm.payment_method,
          payment_date: bookingForm.payment_date,
          reference_number: bookingForm.reference_number || null,
        },
      });
      toast.success(`${res.data.created} bookings created! ${res.data.skipped > 0 ? `(${res.data.skipped} skipped due to conflicts)` : ""}`);
      fetchCalendarData();
      setOpenBooking(false);
      resetBookingForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create recurring bookings");
    }
  };

  // ========== RESOURCES ==========
  const addResource = async () => {
    if (!resourceForm.name || !resourceForm.quantity) { toast.error("Please fill all fields"); return; }
    try {
      await api.post("/resources", { studio_id: selectedStudio.studio_id, name: resourceForm.name, quantity: parseInt(resourceForm.quantity) });
      toast.success("Resource added!");
      fetchResources();
      setResourceForm({ name: "", quantity: "" });
    } catch (error) { toast.error("Failed to add resource"); }
  };

  const updateResource = async () => {
    if (!resourceForm.quantity) { toast.error("Please enter quantity"); return; }
    try {
      await api.put(`/resources/${editingResource.resource_id}`, { ...editingResource, quantity: parseInt(resourceForm.quantity) });
      toast.success("Resource updated!");
      fetchResources();
      setEditingResource(null);
      setResourceForm({ name: "", quantity: "" });
    } catch (error) { toast.error("Failed to update resource"); }
  };

  const toggleResource = (resourceId) => {
    const existing = bookingForm.resources_required.find(r => r.resource_id === resourceId);
    if (existing) {
      setBookingForm({ ...bookingForm, resources_required: bookingForm.resources_required.filter(r => r.resource_id !== resourceId) });
    } else {
      setBookingForm({ ...bookingForm, resources_required: [...bookingForm.resources_required, { resource_id: resourceId, quantity: 1 }] });
    }
  };

  const updateResourceQuantity = (resourceId, quantity) => {
    setBookingForm({
      ...bookingForm,
      resources_required: bookingForm.resources_required.map(r =>
        r.resource_id === resourceId ? { ...r, quantity: parseInt(quantity) || 1 } : r
      )
    });
  };

  const toggleEditResource = (resourceId) => {
    const existing = (editForm.resources_required || []).find(r => r.resource_id === resourceId);
    if (existing) {
      setEditForm({ ...editForm, resources_required: editForm.resources_required.filter(r => r.resource_id !== resourceId) });
    } else {
      setEditForm({ ...editForm, resources_required: [...(editForm.resources_required || []), { resource_id: resourceId, quantity: 1 }] });
    }
  };

  const updateEditResourceQuantity = (resourceId, quantity) => {
    setEditForm({
      ...editForm,
      resources_required: (editForm.resources_required || []).map(r =>
        r.resource_id === resourceId ? { ...r, quantity: parseInt(quantity) || 1 } : r
      )
    });
  };

  // ========== SCROLL SYNC ==========
  useEffect(() => {
    if (calendarData) {
      const now = new Date();
      const scrollTarget = Math.max(0, (now.getHours() * 2 - 2)) * 58;
      setTimeout(() => {
        const grid = document.getElementById('calendar-time-grid');
        if (grid) grid.scrollTop = scrollTarget;
      }, 300);
      const grid = document.getElementById('calendar-time-grid');
      const labels = document.getElementById('calendar-time-labels');
      if (grid && labels) {
        const onScroll = () => { labels.scrollTop = grid.scrollTop; };
        grid.addEventListener('scroll', onScroll);
        return () => grid.removeEventListener('scroll', onScroll);
      }
    }
  }, [calendarData]);

  const timeSlots = Array.from({ length: 48 }, (_, i) => {
    const totalMinutes = i * 30;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const bookingTotal = calcTotalAmount(bookingForm.agreed_rent, bookingForm.discount, bookingForm.discount_type);
  const editTotal = calcTotalAmount(editForm.agreed_rent, editForm.discount, editForm.discount_type);

  return (
    <div className="min-h-screen bg-slate-50 md:pl-64">
      <Sidebar user={user} onLogout={onLogout} />
      <BottomNav user={user} onLogout={onLogout} />

      <div className="p-4 md:p-8 pb-20 md:pb-8">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-4xl font-bold font-heading text-slate-900">Studio Calendar</h1>
          <p className="text-sm text-slate-600 mt-1">Manage bookings & resources</p>
        </div>

        {/* Studio & Date Selection */}
        <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100 mb-6">
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1 space-y-2 w-full">
              <Label className="text-xs sm:text-sm">Select Studio</Label>
              <select
                data-testid="studio-select"
                value={selectedStudio?.studio_id || ""}
                onChange={(e) => setSelectedStudio(studios.find(s => s.studio_id === e.target.value))}
                className="w-full h-10 sm:h-12 rounded-xl border border-slate-200 px-3 bg-white text-sm"
              >
                {studios.map(s => <option key={s.studio_id} value={s.studio_id}>{s.name}</option>)}
              </select>
            </div>
            <div className="flex-1 space-y-2 w-full">
              <Label className="text-xs sm:text-sm">Select Date</Label>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="rounded-full h-10 sm:h-12 w-10 sm:w-12 flex-shrink-0" onClick={() => setSelectedDate(format(addDays(new Date(selectedDate), -1), 'yyyy-MM-dd'))}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Input
                  data-testid="date-picker"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="h-10 sm:h-12 rounded-xl text-sm flex-1"
                />
                <Button size="sm" variant="outline" className="rounded-full h-10 sm:h-12 w-10 sm:w-12 flex-shrink-0" onClick={() => setSelectedDate(format(addDays(new Date(selectedDate), 1), 'yyyy-MM-dd'))}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {canEditCalendar && (
              <Button data-testid="manage-resources-btn" onClick={() => setOpenResource(true)} className="h-10 sm:h-12 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs sm:text-sm w-full sm:w-auto whitespace-nowrap">
                <Users className="w-4 h-4 mr-1" /> Resources
              </Button>
            )}
          </div>
        </div>

        {/* Calendar Grid */}
        {calendarData && selectedStudio && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-4 sm:p-6 pb-3">
              <h2 className="text-lg sm:text-2xl font-bold font-heading text-slate-900">{calendarData.studio.name}</h2>
              <p className="text-xs sm:text-sm text-slate-600">{format(new Date(selectedDate), 'EEEE, MMMM d, yyyy')}</p>
            </div>

            <div className="flex" id="calendar-scroll-container">
              {/* Time Legend - Fixed left column */}
              <div className="flex-shrink-0 w-16 z-20 bg-white">
                <div className="h-14 border-b border-slate-100"></div>
                <div className="overflow-hidden max-h-[60vh]" id="calendar-time-labels">
                  {timeSlots.map(time => {
                    const [h, m] = time.split(":");
                    const hour = parseInt(h, 10);
                    const period = hour >= 12 ? "PM" : "AM";
                    const h12 = hour % 12 || 12;
                    return (
                      <div key={time} className="h-[58px] text-[11px] font-medium text-slate-500 pl-2 pr-1 flex flex-col items-start pt-1 leading-tight">
                        <span>{h12}:{m}</span>
                        <span className="text-[9px] text-slate-400">{period}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Scrollable rooms area */}
              <div className="flex-1 overflow-x-auto">
                <div style={{ minWidth: `${calendarData.studio.rooms.length * 180}px` }}>
                  {/* Room Headers - sticky on vertical scroll */}
                  <div className="grid gap-1 pb-2 sticky top-0 bg-white z-20 border-b border-slate-100" style={{ gridTemplateColumns: `repeat(${calendarData.studio.rooms.length}, minmax(160px, 1fr))` }}>
                    {calendarData.studio.rooms.map(room => (
                      <div key={room.room_id} className="font-bold text-slate-900 p-2 bg-violet-50 rounded-lg text-center text-sm">
                        {room.name}
                        <div className="text-[10px] text-slate-500 font-normal">Cap: {room.capacity}</div>
                      </div>
                    ))}
                  </div>

                  {/* Grid Body */}
                  <div className="overflow-y-auto max-h-[60vh]" id="calendar-time-grid">
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${calendarData.studio.rooms.length}, minmax(160px, 1fr))`, gap: '4px' }}>
                      {calendarData.studio.rooms.map(room => (
                        <div key={room.room_id} className="relative" style={{ display: 'grid', gridTemplateRows: `repeat(${timeSlots.length}, 56px)`, gap: '2px' }}>
                          {timeSlots.map((time) => (
                            <div key={`bg-${time}`} className="border border-dashed border-slate-200 rounded-lg" />
                          ))}

                          {/* Event blocks */}
                          {getItemsForRoom(room.room_id).map((item, itemIndex) => {
                            const startMinutes = timeToMinutes(item.start_time);
                            const endMinutes = timeToMinutes(item.end_time);
                            const slotSpan = Math.ceil((endMinutes - startMinutes) / 30);
                            const topPosition = (startMinutes / 30) * 58;
                            const heightSize = (slotSpan * 58) - 2;
                            
                            const statusColors = item.type === "rental"
                              ? PAYMENT_STATUS_COLORS[item.rent_payment_status || "pending"]
                              : null;
                            const isRental = item.type === "rental";
                            const isClickableClass = item.type === "class" && !!item.batch_id && canEditCalendar;
                            const isClickable = isRental || isClickableClass;
                            // Feb 2026 — when admin explicitly sets a color
                            // on a rental, override the status-driven tint.
                            const rentalCustomColor = isRental && item.color ? item.color : null;
                            return (
                              <div
                                key={`event-${itemIndex}`}
                                data-testid={`calendar-event-${itemIndex}`}
                                onClick={() => {
                                  if (isRental) {
                                    openBookingDetails(item);
                                  } else if (isClickableClass) {
                                    openClassEdit(item);
                                  }
                                }}
                                className={`absolute left-0 right-0 p-1.5 sm:p-2 rounded-lg text-xs overflow-hidden z-20 ${isClickable ? "cursor-pointer hover:opacity-90" : ""}`}
                                style={{
                                  top: `${topPosition}px`,
                                  height: `${heightSize}px`,
                                  backgroundColor: rentalCustomColor
                                    ? `${rentalCustomColor}20`
                                    : isRental ? statusColors?.bg : item.type === 'class' ? `${item.color}20` : '#E2E8F0',
                                  borderLeft: `3px solid ${rentalCustomColor
                                    ? rentalCustomColor
                                    : isRental ? statusColors?.border : item.type === 'class' ? item.color : '#64748B'}`,
                                }}
                              >
                                {item.type === 'blocked' ? (
                                  <p className="font-medium text-slate-600 text-[10px]">Blocked</p>
                                ) : item.type === 'class' ? (
                                  <>
                                    <div className="flex items-center gap-1">
                                      <p className="font-bold text-slate-900 text-[10px] truncate flex-1">{item.batch_name}</p>
                                      {item.is_substituted && (
                                        <span className="px-1 py-[1px] bg-amber-500 text-white text-[7px] font-bold rounded leading-none flex-shrink-0" title="Substitute instructor">SUB</span>
                                      )}
                                      {item.is_makeup && (
                                        <span className="px-1 py-[1px] bg-violet-500 text-white text-[7px] font-bold rounded leading-none flex-shrink-0" title="Makeup session">M</span>
                                      )}
                                    </div>
                                    {item.instructor?.name && (
                                      <p className="text-[9px] text-slate-600 truncate">{item.instructor.name}</p>
                                    )}
                                    <p className="text-[9px] text-slate-500">{formatTime12(item.start_time)} - {formatTime12(item.end_time)}</p>
                                  </>
                                ) : (
                                  <>
                                    <div className="flex items-center gap-1">
                                      <span className="px-1 py-[1px] bg-green-700 text-white text-[7px] font-bold rounded leading-none flex-shrink-0">RENT</span>
                                      <p className="font-bold text-slate-900 text-[10px] truncate flex-1">{item.renter_name}</p>
                                      {item.notes && <StickyNote className="w-3 h-3 text-amber-600 flex-shrink-0" />}
                                    </div>
                                    <p className="text-[9px] text-slate-500">{formatTime12(item.start_time)} - {formatTime12(item.end_time)}</p>
                                    {heightSize > 60 && (
                                      <div className="mt-0.5 flex items-center gap-1">
                                        <span className="text-[11px]">{statusColors?.icon}</span>
                                        <span className={`text-[9px] font-semibold ${statusColors?.text}`}>
                                          {item.rent_payment_status === "paid" ? "Paid" : item.rent_payment_status === "partial" ? `Partial ₹${item.amount_paid || 0}` : `Due: ₹${item.balance_amount || item.total_amount || item.agreed_rent}`}
                                        </span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })}

                          {/* Add booking buttons */}
                          {canEditCalendar && timeSlots.map((time, timeIndex) => {
                            if (isSlotOccupied(room.room_id, time)) return null;
                            return (
                              <button
                                key={`add-${time}`}
                                onClick={() => {
                                  setSelectedRoom(room.room_id);
                                  const startMins = timeToMinutes(time);
                                  const endMins = Math.min(startMins + 60, 23 * 60 + 30);
                                  const endH = Math.floor(endMins / 60).toString().padStart(2, '0');
                                  const endM = (endMins % 60).toString().padStart(2, '0');
                                  setBookingForm(prev => ({ ...prev, start_time: time, end_time: `${endH}:${endM}` }));
                                  setOpenBooking(true);
                                }}
                                className="absolute left-0 right-0 h-[56px] flex items-center justify-center text-slate-300 hover:text-primary hover:bg-violet-50 rounded-lg transition-all z-10"
                                style={{ top: `${timeIndex * 58}px` }}
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>

      <BookingDialog
        open={openBooking}
        onOpenChange={(v) => { if (!v) resetBookingForm(); setOpenBooking(v); }}
        bookingForm={bookingForm}
        setBookingForm={setBookingForm}
        bookingTotal={bookingTotal}
        customerSuggestions={customerSuggestions}
        showSuggestions={showSuggestions}
        setShowSuggestions={setShowSuggestions}
        selectedCustomerId={selectedCustomerId}
        setSelectedCustomerId={setSelectedCustomerId}
        searchCustomers={searchCustomers}
        selectCustomer={selectCustomer}
        resources={resources}
        toggleResource={toggleResource}
        updateResourceQuantity={updateResourceQuantity}
        recurringPreview={recurringPreview}
        setRecurringPreview={setRecurringPreview}
        recurringLoading={recurringLoading}
        previewRecurring={previewRecurring}
        createBooking={createBooking}
        createRecurringBookings={createRecurringBookings}
        selectedDate={selectedDate}
      />

      <BookingDetailsDialog
        open={openDetails}
        onOpenChange={setOpenDetails}
        selectedBooking={selectedBooking}
        selectedStudio={selectedStudio}
        detailsTab={detailsTab}
        setDetailsTab={setDetailsTab}
        formatTime12={formatTime12}
        PAYMENT_STATUS_COLORS={PAYMENT_STATUS_COLORS}
        resources={resources}
        editForm={editForm}
        setEditForm={setEditForm}
        editTotal={editTotal}
        toggleEditResource={toggleEditResource}
        updateEditResourceQuantity={updateEditResourceQuantity}
        saveBookingEdit={saveBookingEdit}
        payForm={payForm}
        setPayForm={setPayForm}
        recordPayment={recordPayment}
        cancelConfirmStep={cancelConfirmStep}
        setCancelConfirmStep={setCancelConfirmStep}
        cancelFuture={cancelFuture}
        setCancelFuture={setCancelFuture}
        initCancelBooking={initCancelBooking}
        executeCancelBooking={executeCancelBooking}
        onBookingMutated={() => fetchCalendarData()}
      />

      <ResourcesDialog
        open={openResource}
        onOpenChange={setOpenResource}
        resources={resources}
        resourceForm={resourceForm}
        setResourceForm={setResourceForm}
        editingResource={editingResource}
        setEditingResource={setEditingResource}
        addResource={addResource}
        updateResource={updateResource}
      />

      <EditSessionDialog
        open={openEditSession}
        onOpenChange={setOpenEditSession}
        calendarItem={editingSessionItem}
        studioId={selectedStudio?.studio_id}
        studioRooms={selectedStudio?.rooms || []}
        instructors={instructorOptions}
        batchDefaults={editingBatchDefaults}
        selectedDate={selectedDate}
        onSaved={() => fetchCalendarData()}
      />
    </div>
  );
};

export default StudioCalendar;
